<template>
  <div class="page-wrap">

    <!-- Loading state -->
    <template v-if="store.loading">
      <div class="no-data-wrap">
        <div class="nd-icon">⏳</div>
        <h2>กำลังโหลดข้อมูล…</h2>
      </div>
    </template>

    <!-- No data state -->
    <template v-else-if="!store.hasData">
      <div class="no-data-wrap">
        <div class="nd-icon">📊</div>
        <h2>ยังไม่มีข้อมูลการวิเคราะห์</h2>
        <p>กรุณาอัปโหลดไฟล์ Excel เพื่อเริ่มต้นวิเคราะห์คะแนนเครดิตลูกค้า</p>
        <a class="btn-primary" href="#" @click.prevent="router.push({ name: 'CsUpload' })">ไปอัปโหลดไฟล์</a>
      </div>
    </template>

    <template v-else>
      <!-- Timestamp -->
      <div class="timestamp">
        <span>{{ fmtTimestamp(store.timestamp) }}</span>
        <button class="guide-btn" title="คู่มือตัวชี้วัด" @click="guideModalOpen = true">
          <span class="guide-btn-lbl">คู่มือตัวชี้วัด</span>?
        </button>
      </div>

      <!-- Stat cards (6 cols) -->
      <div class="section grid-6">
        <div v-for="card in statCards" :key="card.label" class="cs-card">
          <div class="stat-label">
            {{ card.label }}
            <span v-if="card.tooltip" class="info-icon" :title="card.tooltip">ⓘ</span>
          </div>
          <div class="stat-val" :style="{ color: card.color }">{{ card.value }}</div>
          <div v-if="card.sub" class="stat-sub">{{ card.sub }}</div>
        </div>
      </div>

      <!-- Open ticket breakdown by last transaction type -->
      <div v-if="store.totalOpenTickets > 0 && openBreakdown.length > 0" class="section">
        <div class="section-heading">โครงสร้างตั๋วที่เปิดอยู่ทั้งหมด — จำแนกตามธุรกรรมล่าสุดต่อตั๋ว</div>
        <div class="card ob-card">
          <div class="ob-grid">
            <div v-for="ob in openBreakdown" :key="ob.key" class="ob-row">
              <div class="ob-label-wrap">
                <span class="ob-dot" :class="'ob-dot-' + ob.key"></span>
                <span class="ob-lbl">{{ ob.label }}</span>
              </div>
              <div class="ob-bar-wrap">
                <div class="ob-bar-bg">
                  <div
class="ob-bar-fill" :class="'ob-fill-' + ob.key"
                    :style="{ width: Math.round(ob.count / store.totalOpenTickets * 100) + '%' }">
                  </div>
                </div>
              </div>
              <div class="ob-cnt">{{ fmtNum(ob.count) }} ตั๋ว</div>
              <div class="ob-pct">{{ Math.round(ob.count / store.totalOpenTickets * 100) }}%</div>
              <div class="ob-amt">฿{{ fmtBaht(ob.principal) }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Trend analysis (4 cols) -->
      <div class="section">
        <div class="section-heading">Trend Adjustments — สรุปการปรับคะแนนจากแนวโน้ม</div>
        <div class="grid-4">
          <div v-for="tc in trendCards" :key="tc.label" class="cs-card">
            <div class="trend-title">{{ tc.label }}</div>

            <div class="trend-row">
              <div class="trend-lbl">
                <span class="t-pos">บวก ↑ <span class="trend-hint">{{ tc.posAvg !== null ? 'เฉลี่ย +' + tc.posAvg : '' }}</span></span>
                <span style="font-weight:600">{{ tc.posCount }} <span class="trend-cnt">คน ({{ tc.posPct }}%)</span></span>
              </div>
              <div class="trend-bar-bg"><div class="trend-fill" :style="{ width: tc.posPct + '%', background: '#10b981' }"></div></div>
            </div>

            <div class="trend-row">
              <div class="trend-lbl">
                <span class="t-neu">เป็นกลาง</span>
                <span style="font-weight:600">{{ tc.zeroCount }} <span class="trend-cnt">คน ({{ tc.zeroPct }}%)</span></span>
              </div>
              <div class="trend-bar-bg"><div class="trend-fill" :style="{ width: tc.zeroPct + '%', background: '#9ca3af' }"></div></div>
            </div>

            <div class="trend-row">
              <div class="trend-lbl">
                <span class="t-neg">ลบ ↓ <span class="trend-hint">{{ tc.negAvg !== null ? 'เฉลี่ย ' + tc.negAvg : '' }}</span></span>
                <span style="font-weight:600">{{ tc.negCount }} <span class="trend-cnt">คน ({{ tc.negPct }}%)</span></span>
              </div>
              <div class="trend-bar-bg"><div class="trend-fill" :style="{ width: tc.negPct + '%', background: '#ef4444' }"></div></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Overdue section -->
      <div class="section">
        <div class="section-heading">สถานการณ์ตั๋วค้างชำระ — ตั๋วที่เกินกำหนดแล้ว</div>

        <!-- No overdue -->
        <div v-if="totalOverdueChains === 0" class="cs-card" style="text-align:center;padding:28px 24px;margin-bottom:16px">
          <div style="font-size:2rem;margin-bottom:8px">✅</div>
          <div style="font-weight:700;font-size:1rem;color:#059669">ไม่มีตั๋วค้างชำระ</div>
          <div style="font-size:.82rem;color:#6b7280;margin-top:4px">ตั๋วที่ยัง open อยู่ทั้งหมดยังไม่เกินกำหนด</div>
        </div>

        <!-- Overdue stat cards (3 cols) -->
        <div v-else class="grid-3" style="margin-bottom:16px">
          <div class="cs-card">
            <div class="stat-label">ลูกค้าที่มีตั๋วค้าง</div>
            <div class="overdue-stat-val">{{ overdueCustomers }} ราย</div>
            <div class="stat-sub">{{ pct(overdueCustomers, store.totalCustomers) }}% ของลูกค้าทั้งหมด · {{ totalOverdueChains }} ตั๋ว</div>
          </div>
          <div class="cs-card">
            <div class="stat-label">เงินต้นค้างชำระรวม</div>
            <div class="overdue-stat-val">฿{{ fmtBaht(totalOverduePrincipal) }}</div>
            <div class="stat-sub">{{ pct(totalOverduePrincipal, totalOpenPrincipal) }}% ของเงินต้นทั้งหมด · ฿{{ fmtBaht(totalOpenPrincipal) }}</div>
          </div>
          <div class="cs-card">
            <div class="stat-label">เกินกำหนดสูงสุด</div>
            <div class="overdue-stat-val">{{ maxDaysOverdue }} วัน</div>
            <div class="stat-sub">นับจากวันที่ตั๋วหมดอายุ</div>
          </div>
        </div>

        <!-- Tabs card -->
        <div class="cs-card" style="padding:0">
          <div class="tab-bar">
            <button :class="['tab-btn', overdueTab === 'overdue' ? 'active' : '']" @click="overdueTab = 'overdue'">
              ตั๋วค้างชำระ <span :class="['tab-count', overdueCustomers > 0 ? 'red' : '']">{{ overdueCustomers }}</span>
            </button>
            <button :class="['tab-btn', overdueTab === 'attention' ? 'active' : '']" @click="overdueTab = 'attention'">
              รายการที่ต้องติดตาม <span class="tab-count">{{ warningCustomers }}</span>
            </button>
          </div>

          <div class="filter-bar">
            <input v-model="filterName" class="filter-input" type="text" placeholder="🔍 ค้นหาชื่อลูกค้า…" />
            <select v-model="filterTier" class="filter-select">
              <option value="">— Tier ทั้งหมด —</option>
              <option v-for="t in tierOptions" :key="t" :value="t">{{ TIER_DISPLAY[t] }}</option>
            </select>
            <button class="filter-clear" @click="clearFilters">ล้าง</button>
            <span class="filter-result">
              <span v-if="detailLoading" class="filter-loading">กำลังโหลด…</span>
              <template v-else>แสดง {{ Math.min(displayedCount, activeFiltered.length) }} / {{ activeTotalCount }} รายการ</template>
            </span>
          </div>

          <!-- Overdue table -->
          <div v-if="overdueTab === 'overdue'">
            <p v-if="filteredOverdue.length === 0" class="empty-msg">
              <template v-if="detailLoading">กำลังโหลด…</template>
              <template v-else>{{ totalOverdueChains === 0 ? '✅ ไม่มีตั๋วค้างชำระในขณะนี้' : 'ไม่พบรายการที่ตรงกับตัวกรอง' }}</template>
            </p>
            <div v-else ref="tableScrollRef" class="table-wrap">
              <table class="attn-table">
                <thead>
                  <tr>
                    <th>#</th><th>ชื่อลูกค้า</th><th>Tier</th>
                    <th>ตั๋วค้าง</th><th>เงินต้นค้าง (฿)</th><th>เกินกำหนด (วัน)</th>
                    <th>ตั๋วเปิดทั้งหมด</th><th>เงินต้นเปิด (฿)</th><th>เคยหลุด</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
v-for="(r, i) in visibleOverdue" :key="r.customerId || r.customerName"
                      :class="r.maxDaysOverdue > 90 ? 'warn-high' : r.maxDaysOverdue > 30 ? 'warn-med' : ''">
                    <td class="muted-sm">{{ i + 1 }}</td>
                    <td>
                      <a class="cust-link" href="#" @click.prevent="openPortfolio(r)">{{ r.customerName }}</a>
                    </td>
                    <td><span :class="'tier-badge tier-' + r.tierKey">{{ r.tier }}</span></td>
                    <td><span class="overdue-badge">⚠ {{ r.overdueChains }} ตั๋ว</span></td>
                    <td class="overdue-days">{{ fmtBaht(r.overduePrincipal) }}</td>
                    <td class="overdue-days">{{ r.maxDaysOverdue }}</td>
                    <td class="muted-sm">{{ r.openChains }}</td>
                    <td class="muted-sm">{{ fmtBaht(r.openPrincipal) }}</td>
                    <td>
                      <span v-if="r.forfeitedChains > 0" style="color:#d97706;font-weight:700">{{ r.forfeitedChains }} ใบ</span>
                      <span v-else class="muted-sm">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div ref="sentinelRef" style="height:1px"></div>
            </div>
          </div>

          <!-- Attention table -->
          <div v-if="overdueTab === 'attention'">
            <p v-if="filteredWarning.length === 0" class="empty-msg">
              <template v-if="detailLoading">กำลังโหลด…</template>
              <template v-else>✅ ไม่มีลูกค้าที่ต้องติดตามเป็นพิเศษในขณะนี้</template>
            </p>
            <div v-else ref="tableScrollRef" class="table-wrap">
              <table class="attn-table">
                <thead>
                  <tr>
                    <th>#</th><th>ชื่อลูกค้า</th><th>Tier</th>
                    <th>Risk Score</th><th>Value Score</th>
                    <th>Early Warning Adj</th><th>ตั๋วเปิด</th><th>เคยหลุด</th><th>สาเหตุ</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
v-for="(r, i) in visibleWarning" :key="r.customerId || r.customerName"
                      :class="r.earlyWarningAdj <= -20 ? 'warn-high' : r.earlyWarningAdj <= -10 ? 'warn-med' : ''">
                    <td class="muted-sm">{{ i + 1 }}</td>
                    <td>
                      <a class="cust-link" href="#" @click.prevent="openPortfolio(r)">{{ r.customerName }}</a>
                    </td>
                    <td><span :class="'tier-badge tier-' + r.tierKey">{{ r.tier }}</span></td>
                    <td>
                      <div class="mini-bar-wrap">
                        <div class="mini-bar-bg"><div class="mini-bar-fill" :style="{ width: r.riskFinal + '%', background: '#10b981' }"></div></div>
                        <span style="font-weight:600;font-size:.85rem">{{ fmtNum(r.riskFinal, 2) }}</span>
                      </div>
                    </td>
                    <td>
                      <div class="mini-bar-wrap">
                        <div class="mini-bar-bg"><div class="mini-bar-fill" :style="{ width: r.valueFinal + '%', background: '#3b82f6' }"></div></div>
                        <span style="font-weight:600;font-size:.85rem">{{ fmtNum(r.valueFinal, 2) }}</span>
                      </div>
                    </td>
                    <td>
                      <span v-if="r.earlyWarningAdj < 0" style="color:#dc2626;font-weight:700">{{ fmtNum(r.earlyWarningAdj, 1) }}</span>
                      <span v-else class="muted-sm">—</span>
                    </td>
                    <td style="font-weight:600">{{ r.openChains }}</td>
                    <td>
                      <span v-if="r.forfeitedChains > 0" style="color:#d97706;font-weight:700">{{ r.forfeitedChains }} ใบ</span>
                      <span v-else class="muted-sm">—</span>
                    </td>
                    <td style="font-size:.78rem;color:#b45309">{{ buildReasons(r) }}</td>
                  </tr>
                </tbody>
              </table>
              <div ref="sentinelRef" style="height:1px"></div>
            </div>
          </div>

        </div>
      </div>
    </template>

    <!-- Score guide modal -->
    <Transition name="modal">
      <div v-if="guideModalOpen" class="modal-overlay" @click.self="guideModalOpen = false">
        <div class="modal-box">
          <div class="modal-hdr">
            <span>วิธีอ่านตัวชี้วัดภาพรวมพอร์ต</span>
            <button class="modal-close" @click="guideModalOpen = false">✕</button>
          </div>
          <div class="modal-body">

            <!-- Section 1: Stat cards -->
            <div class="guide-section-hdr">ส่วนที่ 1 — ค่าเฉลี่ยพอร์ต (6 การ์ดบน)</div>
            <table class="guide-table">
              <thead>
                <tr><th>การ์ด</th><th>ความหมาย</th><th>วิธีอ่านค่า</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td class="guide-name">Risk เฉลี่ย</td>
                  <td>ค่าเฉลี่ย Risk Final Score ของลูกค้าทุกราย — วัดระดับความเสี่ยงของพอร์ตโดยรวม อิงจากอัตราหลุด, ความถี่, และแนวโน้มพฤติกรรม</td>
                  <td><span class="guide-pos">ยิ่งสูงยิ่งปลอดภัย</span><br><span class="guide-hint">สีเปลี่ยนตาม Band ด้านล่าง</span></td>
                </tr>
                <tr>
                  <td class="guide-name">Value เฉลี่ย</td>
                  <td>ค่าเฉลี่ย Value Final Score ของลูกค้าทุกราย — วัดมูลค่าที่ลูกค้าสร้างให้ร้าน อิงจากดอกเบี้ยสะสม, ความถี่, และแนวโน้มรายได้</td>
                  <td><span class="guide-pos">ยิ่งสูงยิ่งมีมูลค่า</span><br><span class="guide-hint">สีเปลี่ยนตาม Band ด้านล่าง</span></td>
                </tr>
                <tr>
                  <td class="guide-name">DMI เฉลี่ย</td>
                  <td>ค่าเฉลี่ย Data Maturity Index — วัดความสมบูรณ์ของข้อมูลประวัติลูกค้า DMI ต่ำ = ข้อมูลน้อย → คะแนนจะถูกดึงเข้าหา 50 (neutral) เพื่อไม่ให้ตัดสินจากข้อมูลไม่พอ</td>
                  <td><span class="guide-hint">0–100</span><br>ยิ่งสูง ข้อมูลยิ่งครบถ้วน<br>คะแนนยิ่งน่าเชื่อถือ</td>
                </tr>
                <tr class="guide-final-row">
                  <td class="guide-name">⚠ Early Warning</td>
                  <td>จำนวนลูกค้าที่ถูกหักคะแนน Early Warning — คือลูกค้าที่มีตั๋วเปิดอยู่แล้วเริ่มเกินกำหนด ทำให้คะแนนถูกกระทบ (ดูรายละเอียดในส่วนตั๋วค้างชำระด้านล่าง)</td>
                  <td><span class="guide-neg">ยิ่งน้อยยิ่งดี</span><br><span class="guide-hint">0 = ไม่มีความเสี่ยงเฉียบพลัน</span></td>
                </tr>
              </tbody>
            </table>

            <!-- Section 2: Band -->
            <div class="guide-section-hdr" style="margin-top:18px">ส่วนที่ 2 — เกณฑ์สี Band (ใช้กับ Risk และ Value)</div>
            <div class="guide-band-row">
              <div class="guide-band-cell band-high-bg">
                <span class="band-pill band-high">HIGH ≥ 67</span>
                <div class="guide-band-desc">ระดับสูง — ปลอดภัย / มีมูลค่าสูง</div>
              </div>
              <div class="guide-band-cell band-mid-bg">
                <span class="band-pill band-mid">MID 34–66</span>
                <div class="guide-band-desc">ระดับกลาง — ควรติดตาม</div>
              </div>
              <div class="guide-band-cell band-low-bg">
                <span class="band-pill band-low">LOW &lt; 34</span>
                <div class="guide-band-desc">ระดับต่ำ — เสี่ยงสูง / มูลค่าน้อย</div>
              </div>
            </div>

            <!-- Section 3: Trend cards -->
            <div class="guide-section-hdr" style="margin-top:18px">ส่วนที่ 3 — Trend Adjustments (4 การ์ดแนวโน้ม)</div>
            <table class="guide-table">
              <thead>
                <tr><th>การ์ด</th><th>ความหมาย</th><th>บวก ↑ / ลบ ↓</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td class="guide-name">Risk Trend</td>
                  <td>แนวโน้มอัตราหลุดใน 6 เดือนล่าสุด เทียบกับ 6 เดือนก่อนหน้า แสดงสัดส่วนลูกค้าที่ Risk ดีขึ้น / คงที่ / แย่ลง พร้อมค่าปรับเฉลี่ย</td>
                  <td><span class="guide-pos">+ หลุดน้อยลง</span><br><span class="guide-neg">− หลุดมากขึ้น</span></td>
                </tr>
                <tr>
                  <td class="guide-name">ต่อดอก (Renewal)</td>
                  <td>แนวโน้มพฤติกรรมต่อดอก — ต่อตรงเวลา vs. ต่อช้า (เลยกำหนดแต่ยังไม่หลุด) เทียบ 6+6 เดือน</td>
                  <td><span class="guide-pos">+ ต่อตรงเวลาขึ้น</span><br><span class="guide-neg">− ต่อช้าลง</span></td>
                </tr>
                <tr>
                  <td class="guide-name">Early Warning</td>
                  <td>สัดส่วนลูกค้าที่โดนหักคะแนน EW ในรอบนี้ — ตั๋วที่เปิดอยู่เริ่มเกินกำหนด ยิ่งค้างมากสัดส่วนเงินต้น ยิ่งหักมาก</td>
                  <td><span class="guide-neu">เป็นกลาง = ไม่มีค้าง</span><br><span class="guide-neg">− ค้างชำระ 15–60%+</span></td>
                </tr>
                <tr>
                  <td class="guide-name">Value Trend</td>
                  <td>แนวโน้มรายได้ดอกเบี้ยที่ร้านได้รับจากลูกค้า เทียบ 6+6 เดือน แสดงสัดส่วนลูกค้าที่ Value ดีขึ้น / คงที่ / ลดลง</td>
                  <td><span class="guide-pos">+ รายได้เพิ่ม</span><br><span class="guide-neg">− รายได้ลด</span></td>
                </tr>
              </tbody>
            </table>

            <!-- Section 4: Customer Matrix -->
            <div class="guide-section-hdr" style="margin-top:18px">ส่วนที่ 4 — Customer Matrix (Risk × Value → Tier)</div>
            <div class="gm-intro">คะแนน Risk และ Value ถูกแปลงเป็น Band (สูง/กลาง/ต่ำ) แล้วนำมา Cross กันใน Matrix 3×3 เพื่อกำหนด Tier ของลูกค้า</div>
            <div class="gm-wrap">
              <table class="gm-table">
                <thead>
                  <tr>
                    <th class="gm-corner"></th>
                    <th class="gm-col-hdr"><div class="gm-axis-lbl">Value ต่ำ</div><span class="guide-hint">0–33</span></th>
                    <th class="gm-col-hdr"><div class="gm-axis-lbl">Value กลาง</div><span class="guide-hint">34–66</span></th>
                    <th class="gm-col-hdr"><div class="gm-axis-lbl">Value สูง</div><span class="guide-hint">67–100</span></th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="gm-row-hdr"><div class="gm-axis-lbl">เสี่ยงน้อย</div><span class="guide-hint">67–100</span></td>
                    <td class="gm-cell gm-Silver">  <span class="tier-badge tier-Silver">Silver</span>    <div class="gm-policy">อัตราปกติ รักษาความสัมพันธ์</div></td>
                    <td class="gm-cell gm-Gold">    <span class="tier-badge tier-Gold">Gold</span>        <div class="gm-policy">อัตราดี priority service</div></td>
                    <td class="gm-cell gm-Platinum"><span class="tier-badge tier-Platinum">Platinum</span> <div class="gm-policy">อัตราพิเศษ appraisal % สูงขึ้น</div></td>
                  </tr>
                  <tr>
                    <td class="gm-row-hdr"><div class="gm-axis-lbl">เสี่ยงกลาง</div><span class="guide-hint">34–66</span></td>
                    <td class="gm-cell gm-Standard"> <span class="tier-badge tier-Standard">ทั่วไป</span>        <div class="gm-policy">อัตราปกติ</div></td>
                    <td class="gm-cell gm-Preferred"><span class="tier-badge tier-Preferred">ปานกลาง</span>      <div class="gm-policy">อัตราปกติ monitoring เพิ่ม</div></td>
                    <td class="gm-cell gm-Watch">    <span class="tier-badge tier-Watch">ติดตาม</span>         <div class="gm-policy">ดูแลใกล้ชิด จำกัด max principal</div></td>
                  </tr>
                  <tr>
                    <td class="gm-row-hdr"><div class="gm-axis-lbl">เสี่ยงสูง</div><span class="guide-hint">0–33</span></td>
                    <td class="gm-cell gm-Caution">  <span class="tier-badge tier-Caution">ระวัง</span>         <div class="gm-policy">ลด exposure เงื่อนไขเข้มงวด</div></td>
                    <td class="gm-cell gm-Guard">    <span class="tier-badge tier-Guard">เฝ้าระวัง</span>      <div class="gm-policy">จำกัดวงเงิน appraisal เข้มงวด</div></td>
                    <td class="gm-cell gm-Manage">   <span class="tier-badge tier-Manage">ติดตามใกล้ชิด</span> <div class="gm-policy">บาลานซ์รายได้กับความเสี่ยงสูง</div></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="guide-footnote">
              ค่าเฉลี่ยที่แสดงใน "บวก ↑" และ "ลบ ↓" คือค่าปรับเฉลี่ย (avg adj.) ของกลุ่มนั้น เช่น "เฉลี่ย +3.2" หมายความว่าลูกค้ากลุ่มนี้ได้คะแนนเพิ่มเฉลี่ย 3.2 คะแนน
            </div>
          </div>
        </div>
      </div>
    </Transition>

  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useScoreStore } from '@/stores/score.js'
import { RISK_HIGH_THRESHOLD, RISK_LOW_THRESHOLD } from '@/utils/scoreThresholds.js'

const router = useRouter()
const store  = useScoreStore()
const guideModalOpen = ref(false)

function onKeydown(e) { if (e.key === 'Escape') guideModalOpen.value = false }
onMounted(async () => {
  window.addEventListener('keydown', onKeydown)
  await store.init()
  // init() is memoised, so on a revisit it resolves without re-fetching. Ask the store to
  // re-fire any detail page-0 load that got invalidated while it was in flight.
  store.ensureDetailListsLoaded()
})
onUnmounted(() => { window.removeEventListener('keydown', onKeydown); if (observer) observer.disconnect() })

const overdueTab = ref('overdue')
const filterName = ref('')
const filterTier = ref('')

// ── Scroll-to-reveal (50 rows at a time) ────────────────────────
const displayedCount = ref(50)
const sentinelRef    = ref(null)
const tableScrollRef = ref(null)
let   observer       = null

async function loadMore() {
  const active = overdueTab.value === 'overdue' ? filteredOverdue.value : filteredWarning.value
  if (displayedCount.value < active.length) {
    requestAnimationFrame(() => { displayedCount.value += 50 })
    return
  }
  // Locally-loaded rows are exhausted — page in more from the server before giving up.
  const hasMoreOnServer = overdueTab.value === 'overdue' ? store.overdueHasMore : store.warningHasMore
  if (!hasMoreOnServer) return
  const fetchMore = overdueTab.value === 'overdue' ? store.loadMoreOverdue : store.loadMoreWarning
  const loaded = await fetchMore()
  if (loaded) requestAnimationFrame(() => { displayedCount.value += 50 })
}

watch(sentinelRef, (el) => {
  if (observer) { observer.disconnect(); observer = null }
  if (!el) return
  observer = new IntersectionObserver(
    (entries) => { if (entries[0].isIntersecting) loadMore() },
    { root: tableScrollRef.value, rootMargin: '200px', threshold: 0 }
  )
  observer.observe(el)
})

watch([overdueTab, filterName, filterTier], () => { displayedCount.value = 50 })

// The name/tier filter runs server-side (the lists are paged, so filtering only the rows already
// scrolled into memory would silently miss matches further down the list). Debounced like CsUpload.
let filterTimeout = null
watch([filterName, filterTier], ([name, tier]) => {
  clearTimeout(filterTimeout)
  filterTimeout = setTimeout(() => { store.setDetailFilter(name, tier) }, 300)
})
onUnmounted(() => clearTimeout(filterTimeout))

const TIER_ORDER = ['Platinum','Gold','Silver','Watch','Preferred','Standard','Manage','Guard','Caution']
const TIER_DISPLAY = {
  Platinum: 'Platinum', Gold: 'Gold', Silver: 'Silver',
  Watch: 'ติดตาม', Preferred: 'ปานกลาง', Standard: 'ทั่วไป',
  Manage: 'ติดตามใกล้ชิด', Guard: 'เฝ้าระวัง', Caution: 'ระวัง',
}

// ── Format helpers ────────────────────────────────────────────
function fmtNum(n, dec = 0) {
  return Number(n).toLocaleString('th-TH', { minimumFractionDigits: dec, maximumFractionDigits: dec })
}
function fmtBaht(n) {
  const v = Number(n)
  if (v >= 1000000) return fmtNum(v / 1000000, 2) + 'M'
  if (v >= 1000)    return fmtNum(v / 1000, 1) + 'K'
  return fmtNum(v)
}
function pct(n, total) { return total ? ((n / total) * 100).toFixed(0) : '0' }
function bandColor(v)  { return v >= RISK_HIGH_THRESHOLD ? '#059669' : v >= RISK_LOW_THRESHOLD ? '#d97706' : '#dc2626' }

function fmtTimestamp(ts) {
  if (!ts) return ''
  try {
    const [y, m, day] = ts.split('T')[0].split('-').map(Number)
    const date = new Date(y, m - 1, day).toLocaleDateString('th-TH', { day: 'numeric', month: 'long', year: 'numeric' })
    return `📅 ข้อมูล ณ ${date}  ·  ${store.totalCustomers} ลูกค้า`
  } catch { return ts }
}

function buildReasons(r) {
  const rs = []
  if (r.earlyWarningAdj < 0) rs.push(`Early Warning (${r.earlyWarningAdj})`)
  if (r.riskFinal < RISK_LOW_THRESHOLD) rs.push(`Risk ต่ำ (<${RISK_LOW_THRESHOLD})`)
  return rs.join(', ')
}

// ── Tier options for filter ───────────────────────────────────
const tierOptions = computed(() => {
  const present = new Set(Object.keys(store.summary?.tierDistribution ?? {}))
  return TIER_ORDER.filter(t => present.has(t))
})

// ── Stat cards ────────────────────────────────────────────────
const statCards = computed(() => {
  const n        = store.totalCustomers
  const withOpen = store.withOpenCount
  const warnings      = store.summary?.earlyWarningCount ?? store.earlyWarningCustomers
  const overdueCust   = store.overdueCustomers
  const overdueChains = store.totalOverdueChains
  return [
    { label: 'ลูกค้าทั้งหมด',        value: fmtNum(n),                        sub: '',                              color: '#1d4ed8' },
    { label: 'Risk เฉลี่ย',          value: fmtNum(store.avgRisk, 2),          sub: 'ยิ่งสูงยิ่งปลอดภัย',            color: bandColor(store.avgRisk) },
    { label: 'Value เฉลี่ย',         value: fmtNum(store.avgValue, 2),         sub: 'ยิ่งสูงยิ่งมีมูลค่า',            color: bandColor(store.avgValue) },
    { label: 'DMI เฉลี่ย',          value: fmtNum(store.avgDmi, 2),           sub: 'ความสมบูรณ์ข้อมูล',             color: '#0284c7' },
    { label: 'ตั๋วเปิดอยู่ทั้งหมด',  value: fmtNum(store.totalOpenTickets),    sub: 'ลูกค้าที่มีตั๋วเปิดอยู่: ' + fmtNum(withOpen) + ' คน (' + pct(withOpen, n) + '%)', color: '#059669' },
    {
      label:   '⚠ Early Warning',
      value:   fmtNum(warnings),
      sub:     warnings > 0
                 ? 'คะแนนเตือน · ตั๋วค้างชำระ ' + fmtNum(overdueChains) + ' ตั๋ว (' + fmtNum(overdueCust) + ' ราย) ↓'
                 : 'ไม่มี',
      color:   warnings > 0 ? '#dc2626' : '#9ca3af',
      tooltip: warnings > 0
                 ? 'ลูกค้าที่โดนหักคะแนน EW (' + fmtNum(warnings) + ' คน) — มีตั๋วเกินกำหนดในระดับที่กระทบคะแนน\nตั๋วค้างชำระทั้งหมด ' + fmtNum(overdueChains) + ' ตั๋ว จาก ' + fmtNum(overdueCust) + ' ราย → ดูรายละเอียดในส่วน "สถานการณ์ตั๋วค้างชำระ" ด้านล่าง'
                 : null,
    },
  ]
})

// ── Trend cards ───────────────────────────────────────────────
const trendCards = computed(() => {
  const n = store.totalCustomers || 1
  return [
    { label: 'Risk Trend',        breakdown: store.riskTrend },
    { label: 'ต่อดอก (Renewal)', breakdown: store.renewalTrend },
    { label: 'Early Warning',     breakdown: store.earlyWarningTrend },
    { label: 'Value Trend',       breakdown: store.valueTrend },
  ].map(f => {
    const bd = f.breakdown ?? { posCount: 0, negCount: 0, zeroCount: 0, posAvg: 0, negAvg: 0 }
    return {
      label:     f.label,
      posCount:  bd.posCount,
      negCount:  bd.negCount,
      zeroCount: bd.zeroCount,
      posPct:    Math.round(bd.posCount  / n * 100),
      negPct:    Math.round(bd.negCount  / n * 100),
      zeroPct:   Math.round(bd.zeroCount / n * 100),
      posAvg:    bd.posCount ? bd.posAvg.toFixed(1) : null,
      negAvg:    bd.negCount ? bd.negAvg.toFixed(1) : null,
    }
  })
})

// ── Open ticket breakdown ─────────────────────────────────────
const openBreakdown = computed(() => {
  const total = store.totalOpenTickets
  if (total === 0) return []
  const s = store.summary
  const fallback = s ? null : store.results.reduce((acc, x) => {
    acc.openByPawn += x.openByPawn || 0
    acc.principalByPawn += x.principalByPawn || 0
    acc.openByRenew += x.openByRenew || 0
    acc.principalByRenew += x.principalByRenew || 0
    acc.openByReduce += x.openByReduce || 0
    acc.principalByReduce += x.principalByReduce || 0
    acc.openByIncrease += x.openByIncrease || 0
    acc.principalByIncrease += x.principalByIncrease || 0
    return acc
  }, {
    openByPawn: 0,
    principalByPawn: 0,
    openByRenew: 0,
    principalByRenew: 0,
    openByReduce: 0,
    principalByReduce: 0,
    openByIncrease: 0,
    principalByIncrease: 0,
  })
  return [
    { key: 'pawn',     label: 'จำนำ (ใหม่)', count: s?.openByPawn     ?? fallback?.openByPawn     ?? 0, principal: s?.principalByPawn     ?? fallback?.principalByPawn     ?? 0 },
    { key: 'renew',    label: 'ต่อดอก',       count: s?.openByRenew    ?? fallback?.openByRenew    ?? 0, principal: s?.principalByRenew    ?? fallback?.principalByRenew    ?? 0 },
    { key: 'reduce',   label: 'ลดต้น',        count: s?.openByReduce   ?? fallback?.openByReduce   ?? 0, principal: s?.principalByReduce   ?? fallback?.principalByReduce   ?? 0 },
    { key: 'increase', label: 'เพิ่มต้น',     count: s?.openByIncrease ?? fallback?.openByIncrease ?? 0, principal: s?.principalByIncrease ?? fallback?.principalByIncrease ?? 0 },
  ].filter(b => b.count > 0)
})

// ── Overdue stats ─────────────────────────────────────────────
const overdueCustomers      = computed(() => store.overdueCustomers)
const warningCustomers      = computed(() => store.summary?.earlyWarningCount ?? store.earlyWarningCustomers)
const totalOverdueChains    = computed(() => store.totalOverdueChains)
const totalOverduePrincipal = computed(() => store.totalOverduePrincipal)
const totalOpenPrincipal    = computed(() => store.totalOpenPrincipal)
const maxDaysOverdue        = computed(() => store.maxDaysOverdue)

// ── Table lists ───────────────────────────────────────────────
const overdueList = computed(() => store.overdueList)
const warningList = computed(() => store.warningList)

// ── Filter helpers ────────────────────────────────────────────
// Rows arrive already filtered by the server (store.setDetailFilter), so there is nothing to
// filter client-side — only the active tab's list is sliced for display.
function customerRouteId(r) { return store.routeCustomerId(r) }

// CsPortfolio reads its customer from the URL so a reload can restore it; selectedCustomerId is
// in-memory only and is set here just for the pages that still read it.
function openPortfolio(row) {
  const id = customerRouteId(row)
  store.selectedCustomerId = id
  router.push({ name: 'CsPortfolio', query: { cus: id } })
}

const filteredOverdue = computed(() => overdueList.value)
const filteredWarning = computed(() => warningList.value)
const activeFiltered  = computed(() => overdueTab.value === 'overdue' ? filteredOverdue.value : filteredWarning.value)
// Denominator for "แสดง X / Y": the server's total for the current filter, which is the full
// match count — activeFiltered.length only covers the pages scrolled in so far.
const activeTotalCount = computed(() => overdueTab.value === 'overdue' ? store.overdueTotal : store.warningTotal)
const detailLoading   = computed(() => overdueTab.value === 'overdue' ? store.overdueLoadingMore : store.warningLoadingMore)
const visibleOverdue  = computed(() => overdueTab.value === 'overdue' ? activeFiltered.value.slice(0, displayedCount.value) : [])
const visibleWarning  = computed(() => overdueTab.value === 'attention' ? activeFiltered.value.slice(0, displayedCount.value) : [])
function clearFilters() { filterName.value = ''; filterTier.value = '' }
</script>

<style scoped>
/* ── Page wrapper ────────────────────────────────────────────── */
.page-wrap { width: 100%; padding: 16px 24px; }

/* ── No data state ───────────────────────────────────────────── */
.no-data-wrap { text-align: center; padding: 80px 24px; }
.nd-icon { font-size: 3.5rem; margin-bottom: 16px; }
.no-data-wrap h2 { font-size: 1.3rem; margin-bottom: 8px; }
.no-data-wrap p  { color: #6b7280; margin-bottom: 24px; }
.btn-primary {
  display: inline-block; padding: 11px 28px;
  background: #1d4ed8; color: #fff; text-decoration: none;
  border-radius: 7px; font-weight: 600; font-size: .95rem;
}
.btn-primary:hover { background: #1e40af; }

/* ── Timestamp ───────────────────────────────────────────────── */
.timestamp { font-size: .8rem; color: #6b7280; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; gap: 6px; }

/* ── Grid layouts ────────────────────────────────────────────── */
.section  { margin-bottom: 20px; }
.grid-3   { display: grid; grid-template-columns: repeat(3,1fr); gap: 16px; }
.grid-4   { display: grid; grid-template-columns: repeat(4,1fr); gap: 16px; }
.grid-6   { display: grid; grid-template-columns: repeat(6,1fr); gap: 14px; }

/* ── Card ────────────────────────────────────────────────────── */
.cs-card {
  background: #fff; border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,.08), 0 4px 16px rgba(0,0,0,.06);
  padding: 20px 22px;
  display: block; border: none;
}

/* ── Stat cards ──────────────────────────────────────────────── */
.stat-label { font-size: .76rem; color: #6b7280; text-transform: uppercase; letter-spacing: .4px; display: flex; align-items: center; gap: 4px; }
.info-icon  { font-size: .8rem; color: #9ca3af; cursor: help; user-select: none; flex-shrink: 0; }
.stat-val   { font-size: 1.9rem; font-weight: 800; margin-top: 4px; line-height: 1; }
.stat-sub   { font-size: .75rem; color: #6b7280; margin-top: 4px; }

/* ── Section heading ─────────────────────────────────────────── */
.section-heading { font-size: .78rem; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: .5px; margin-bottom: 12px; }

/* ── Trend card ──────────────────────────────────────────────── */
.trend-title { font-size: .74rem; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: .4px; margin-bottom: 14px; }
.trend-row   { margin-bottom: 9px; }
.trend-lbl   { display: flex; justify-content: space-between; font-size: .78rem; margin-bottom: 3px; }
.trend-hint  { font-size: .72rem; font-weight: 400; color: #6b7280; }
.trend-cnt   { color: #9ca3af; font-weight: 400; font-size: .75rem; }
.trend-bar-bg{ height: 8px; background: #f3f4f6; border-radius: 4px; overflow: hidden; }
.trend-fill  { height: 100%; border-radius: 4px; transition: width .4s ease; }
.t-pos { color: #059669; font-weight: 600; }
.t-neu { color: #9ca3af; font-weight: 600; }
.t-neg { color: #dc2626; font-weight: 600; }

/* ── Overdue stat cards ──────────────────────────────────────── */
.overdue-stat-val { font-size: 1.7rem; font-weight: 800; color: #b91c1c; margin-top: 4px; line-height: 1; }

/* ── Tab bar ─────────────────────────────────────────────────── */
.tab-bar { display: flex; gap: 4px; border-bottom: 2px solid #e2e6ea; padding: 0 16px; }
.tab-btn {
  padding: 9px 18px; font-size: .84rem; font-weight: 600;
  background: none; border: none; border-bottom: 2px solid transparent;
  margin-bottom: -2px; cursor: pointer; color: #6b7280;
  border-radius: 6px 6px 0 0; transition: color .15s, border-color .15s;
}
.tab-btn:hover { color: #1a1d23; }
.tab-btn.active { color: #1d4ed8; border-bottom-color: #1d4ed8; }
.tab-count { display: inline-block; padding: 1px 7px; border-radius: 10px; font-size: .72rem; font-weight: 700; background: #e5e7eb; color: #374151; margin-left: 5px; }
.tab-count.red { background: #fee2e2; color: #b91c1c; }

/* ── Filter bar ──────────────────────────────────────────────── */
.filter-bar { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; padding: 12px 16px; border-bottom: 1px solid #e2e6ea; background: #fafbff; }
.filter-input  { padding: 7px 12px; border: 1px solid #e2e6ea; border-radius: 6px; font-size: .84rem; outline: none; min-width: 220px; flex: 1; }
.filter-input:focus { border-color: #1d4ed8; box-shadow: 0 0 0 2px rgba(29,78,216,.1); }
.filter-select { padding: 7px 12px; border: 1px solid #e2e6ea; border-radius: 6px; font-size: .84rem; outline: none; background: #fff; cursor: pointer; }
.filter-clear  { padding: 7px 12px; font-size: .82rem; color: #6b7280; background: none; border: 1px solid #e2e6ea; border-radius: 6px; cursor: pointer; }
.filter-clear:hover { color: #1a1d23; border-color: #9ca3af; }
.filter-result { font-size: .78rem; color: #6b7280; white-space: nowrap; margin-left: auto; }
.filter-loading { color: #2563eb; font-weight: 600; }

.empty-msg { color: #6b7280; font-size: .9rem; padding: 12px 16px; }

/* ── Table ───────────────────────────────────────────────────── */
.table-wrap { overflow-x: auto; overflow-y: auto; max-height: 1170px; }
.attn-table { width: 100%; border-collapse: collapse; font-size: .85rem; line-height: 1.55; }
.attn-table thead th {
  background: #f8faff; padding: 9px 12px; text-align: left;
  font-size: .74rem; font-weight: 700; color: #6b7280;
  text-transform: uppercase; letter-spacing: .4px;
  border-bottom: 2px solid #e2e6ea; white-space: nowrap;
  position: sticky; top: 0; z-index: 1;
}
.attn-table tbody tr  { border-bottom: 1px solid #e2e6ea; transition: background .1s; }
.attn-table tbody tr:last-child { border-bottom: none; }
.attn-table tbody tr:hover { background: #fafbff; }
.attn-table td { padding: 10px 12px; vertical-align: middle; line-height: 1.55; }
.warn-high { background: #fff1f1 !important; }
.warn-med  { background: #fffbeb !important; }

/* ── Guide button ────────────────────────────────────────────── */
.guide-btn {
  border-radius: 20px; padding: 3px 10px;
  border: 1.5px solid #c7d2fe; background: #eef2ff;
  color: #4f46e5; font-size: .72rem; font-weight: 800;
  cursor: pointer; flex-shrink: 0; line-height: 1;
  display: flex; align-items: center; gap: 5px;
  transition: background .15s, border-color .15s, transform .15s;
}
.guide-btn:hover { background: #4f46e5; border-color: #4f46e5; color: #fff; transform: scale(1.02); }
.guide-btn-lbl { font-size: .7rem; font-weight: 600; }

/* ── Score guide modal ───────────────────────────────────────── */
.modal-overlay {
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0,0,0,.45);
  display: flex; align-items: center; justify-content: center; padding: 20px;
}
.modal-box {
  background: #fff; border-radius: 14px; box-shadow: 0 20px 60px rgba(0,0,0,.25);
  width: 100%; max-width: 780px; max-height: 85vh;
  display: flex; flex-direction: column; overflow: hidden;
}
.modal-hdr {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 22px; border-bottom: 1px solid #e2e6ea;
  font-size: .8rem; font-weight: 700; color: #6b7280;
  text-transform: uppercase; letter-spacing: .6px; flex-shrink: 0;
}
.modal-close {
  width: 28px; height: 28px; border-radius: 50%;
  border: none; background: #f3f4f6; color: #6b7280;
  font-size: .8rem; cursor: pointer; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  transition: background .15s, color .15s;
}
.modal-close:hover { background: #fee2e2; color: #dc2626; }
.modal-body { overflow-y: auto; }
.modal-enter-active { transition: opacity .2s ease; }
.modal-leave-active { transition: opacity .18s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
.modal-enter-active .modal-box,
.modal-leave-active .modal-box { transition: transform .2s ease, opacity .2s ease; }
.modal-enter-from .modal-box { transform: scale(.95) translateY(12px); opacity: 0; }
.modal-leave-to   .modal-box { transform: scale(.97) translateY(6px);  opacity: 0; }

/* ── Guide table ─────────────────────────────────────────────── */
.guide-table { width: 100%; border-collapse: collapse; font-size: .8rem; }
.guide-table thead tr { background: #f8fafc; border-bottom: 2px solid #e2e6ea; }
.guide-table thead th { padding: 9px 14px; text-align: left; font-size: .67rem; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: .5px; }
.guide-table tbody tr { border-bottom: 1px solid #f1f5f9; }
.guide-table tbody tr:last-child { border-bottom: none; }
.guide-table tbody td { padding: 9px 14px; vertical-align: top; line-height: 1.55; color: #374151; }
.guide-table tbody td:first-child { width: 160px; }
.guide-table tbody td:last-child  { width: 220px; white-space: nowrap; }
.guide-name { font-weight: 700; color: #1a1d23 !important; font-size: .78rem; white-space: nowrap; }
.guide-sep td { padding: 0 !important; border-top: 2px solid #e2e6ea !important; }
.guide-final-row td { background: #f0f9ff; }
.guide-pos  { color: #059669; font-weight: 600; }
.guide-neg  { color: #dc2626; font-weight: 600; }
.guide-neu  { color: #9ca3af; font-weight: 600; }
.guide-hint { color: #6366f1; font-weight: 600; }
.band-pill  { display: inline-block; padding: 1px 8px; border-radius: 20px; font-size: .7rem; font-weight: 700; margin-right: 4px; margin-bottom: 2px; }
.band-high  { background: #d1fae5; color: #065f46; }
.band-mid   { background: #fef3c7; color: #92400e; }
.band-low   { background: #fee2e2; color: #991b1b; }

/* ── Guide modal sections ────────────────────────────────────── */
.guide-section-hdr {
  font-size: .67rem; font-weight: 700; color: #6b7280;
  text-transform: uppercase; letter-spacing: .5px;
  padding: 10px 14px 6px; background: #f8fafc;
  border-top: 1px solid #e2e6ea; border-bottom: 1px solid #e2e6ea;
}
.guide-band-row {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 0;
  border-bottom: 1px solid #e2e6ea;
}
.guide-band-cell {
  padding: 12px 14px; text-align: center;
  border-right: 1px solid #e2e6ea;
}
.guide-band-cell:last-child { border-right: none; }
.band-high-bg { background: #f0fdf4; }
.band-mid-bg  { background: #fffbeb; }
.band-low-bg  { background: #fef2f2; }
.guide-band-desc { font-size: .72rem; color: #6b7280; margin-top: 5px; line-height: 1.4; }
/* ── Customer Matrix ─────────────────────────────────────────── */
.gm-intro { font-size: .78rem; color: #6b7280; margin-bottom: 12px; line-height: 1.55; }
.gm-wrap  { overflow-x: auto; }
.gm-table { width: 100%; border-collapse: collapse; font-size: .78rem; }
.gm-corner { width: 90px; }
.gm-col-hdr { text-align: center; padding: 8px 10px; background: #f8fafc; border: 1px solid #e2e8f0; font-weight: 700; font-size: .68rem; color: #374151; }
.gm-row-hdr { padding: 10px 12px; background: #f8fafc; border: 1px solid #e2e8f0; font-weight: 700; font-size: .68rem; color: #374151; white-space: nowrap; vertical-align: middle; }
.gm-axis-lbl { font-size: .72rem; font-weight: 700; color: #1a1d23; }
.gm-cell { padding: 10px 12px; border: 1px solid #e2e8f0; vertical-align: top; text-align: center; }
.gm-policy { font-size: .68rem; color: #6b7280; margin-top: 5px; line-height: 1.4; }
.gm-Silver   { background: #f8fafc; }
.gm-Gold     { background: #fffef0; }
.gm-Platinum { background: #f5f3ff; }
.gm-Standard { background: #f0f4ff; }
.gm-Preferred{ background: #f0f9ff; }
.gm-Watch    { background: #eff6ff; }
.gm-Caution  { background: #fff5f5; }
.gm-Guard    { background: #fffbeb; }
.gm-Manage   { background: #fff1f2; }
.tier-Caution { background: #fde8e8; color: #991b1b; }
.tier-Guard   { background: #fef3c7; color: #92400e; }
.guide-footnote {
  font-size: .72rem; color: #9ca3af; padding: 10px 14px;
  border-top: 1px solid #f1f5f9; line-height: 1.55;
  font-style: italic;
}
.muted-sm  { color: #6b7280; font-size: .85rem; }

/* ── Tier badge ──────────────────────────────────────────────── */
.tier-badge    { display: inline-block; padding: 2px 9px; border-radius: 20px; font-size: .74rem; font-weight: 700; white-space: nowrap; }
.tier-Platinum { background: #ede9fe; color: #6d28d9; }
.tier-Gold     { background: #fef9c3; color: #92400e; }
.tier-Silver   { background: #f1f5f9; color: #475569; }
.tier-Watch    { background: #dbeafe; color: #1d4ed8; }
.tier-Preferred{ background: #e0f2fe; color: #075985; }
.tier-Standard { background: #eff6ff; color: #1e40af; }
.tier-Manage   { background: #fee2e2; color: #b91c1c; }
.tier-Guard    { background: #fef3c7; color: #92400e; }
.tier-Caution  { background: #fde8e8; color: #991b1b; }

/* ── Customer link ───────────────────────────────────────────── */
.cust-link { color: #1d4ed8; text-decoration: none; font-weight: 600; line-height: 1.55; }
.cust-link:hover { text-decoration: underline; }

/* ── Overdue cells ───────────────────────────────────────────── */
.overdue-badge { display: inline-flex; align-items: center; gap: 3px; padding: 2px 8px; border-radius: 4px; font-size: .72rem; font-weight: 700; background: #fee2e2; color: #b91c1c; white-space: nowrap; }
.overdue-days  { font-size: .78rem; color: #b91c1c; font-weight: 700; }

/* ── Mini bar ────────────────────────────────────────────────── */
.mini-bar-wrap { display: flex; align-items: center; gap: 6px; }
.mini-bar-bg   { width: 48px; height: 5px; background: #e5e7eb; border-radius: 3px; overflow: hidden; }
.mini-bar-fill { height: 100%; border-radius: 3px; }

/* ── Open ticket breakdown ───────────────────────────────────── */
.ob-card  { padding: 20px 24px; }
.ob-grid  { display: flex; flex-direction: column; gap: 12px; }
.ob-row   { display: flex; align-items: center; gap: 12px; }
.ob-label-wrap { display: flex; align-items: center; gap: 8px; min-width: 140px; }
.ob-dot   { width: 11px; height: 11px; border-radius: 50%; flex-shrink: 0; }
.ob-dot-pawn     { background: #3b82f6; }
.ob-dot-renew    { background: #10b981; }
.ob-dot-reduce   { background: #f59e0b; }
.ob-dot-increase { background: #8b5cf6; }
.ob-lbl   { font-size: .86rem; color: var(--text, #1a1d23); font-weight: 500; }
.ob-bar-wrap { flex: 1; }
.ob-bar-bg   { height: 10px; background: #f3f4f6; border-radius: 5px; overflow: hidden; }
.ob-bar-fill { height: 100%; border-radius: 5px; transition: width .35s ease; }
.ob-fill-pawn     { background: linear-gradient(90deg, #93c5fd, #3b82f6); }
.ob-fill-renew    { background: linear-gradient(90deg, #6ee7b7, #10b981); }
.ob-fill-reduce   { background: linear-gradient(90deg, #fcd34d, #f59e0b); }
.ob-fill-increase { background: linear-gradient(90deg, #c4b5fd, #8b5cf6); }
.ob-cnt  { font-size: .85rem; font-weight: 700; min-width: 70px; text-align: right; color: #1a1d23; }
.ob-pct  { font-size: .8rem;  color: #6b7280; min-width: 42px; text-align: right; }
.ob-amt  { font-size: .85rem; font-weight: 700; min-width: 90px; text-align: right; color: #374151; }
</style>
